"use client";

import React, { useState, useCallback, useEffect } from "react";
import {
  v1 as uuidv1,
  v4 as uuidv4,
  v5 as uuidv5,
  v7 as uuidv7,
  validate as uuidValidate,
  version as uuidVersion,
} from "uuid";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/app/components/ui/card";
import { Button } from "@/app/components/ui/button";
import { Input } from "@/app/components/ui/input";
import { Label } from "@/app/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/app/components/ui/select";
import { CopyButton } from "@/app/components/ui/copy-button";
import { QuantitySlider } from "@/app/components/ui/quantity-slider";
import { Switch } from "@/app/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/app/components/ui/tabs";
import {
  Sparkles,
  RotateCcw,
  RefreshCw,
  Download,
  CheckCircle,
  XCircle,
  Clock,
  Hash,
  Key,
} from "lucide-react";
import { toast } from "sonner";

const V5_NAMESPACES = {
  DNS: "6ba7b810-9dad-11d1-80b4-00c04fd430c8",
  URL: "6ba7b811-9dad-11d1-80b4-00c04fd430c8",
  OID: "6ba7b812-9dad-11d1-80b4-00c04fd430c8",
  X500: "6ba7b814-9dad-11d1-80b4-00c04fd430c8",
} as const;

const NIL_UUID = "00000000-0000-0000-0000-000000000000";
const MAX_UUID = "ffffffff-ffff-ffff-ffff-ffffffffffff";

const HISTORY_SIZE = 50;

type UUIDVersion = "v1" | "v4" | "v5" | "v7";
type V5NamespaceKey = keyof typeof V5_NAMESPACES | "custom";

function formatUUID(
  raw: string,
  options: { uppercase: boolean; hyphens: boolean; braces: boolean }
): string {
  let s = raw;
  if (!options.hyphens) s = s.replace(/-/g, "");
  if (options.uppercase) s = s.toUpperCase();
  else s = s.toLowerCase();
  if (options.braces) s = `{${s}}`;
  return s;
}

function decodeV1Timestamp(uuid: string): Date | null {
  const hex = uuid.replace(/-/g, "").toLowerCase();
  if (hex.length !== 32) return null;
  const timeLow = parseInt(hex.slice(0, 8), 16);
  const timeMid = parseInt(hex.slice(8, 12), 16);
  const timeHigh = parseInt(hex.slice(12, 16), 16) & 0x0fff;
  const ticks = (timeHigh * 0x100000000 + timeMid) * 0x10000 + timeLow;
  const UUID_EPOCH_MS = 0x01b21dd213814000; // 100ns units between 1582-10-15 and 1970-01-01
  const ms = (ticks - UUID_EPOCH_MS) / 10000;
  return new Date(ms);
}

function decodeV7Timestamp(uuid: string): Date | null {
  const hex = uuid.replace(/-/g, "").toLowerCase();
  if (hex.length !== 32) return null;
  const version = parseInt(hex.slice(12, 13), 16);
  if (version !== 7) return null;
  const unixMs = parseInt(hex.slice(0, 12), 16);
  return new Date(unixMs);
}

export default function UUIDGeneratorTool() {
  const [version, setVersion] = useState<UUIDVersion>("v4");
  const [v5NamespaceKey, setV5NamespaceKey] = useState<V5NamespaceKey>("DNS");
  const [v5NamespaceCustom, setV5NamespaceCustom] = useState("");
  const [v5Name, setV5Name] = useState("");
  const [count, setCount] = useState(5);
  const [generatedUUIDs, setGeneratedUUIDs] = useState<string[]>([]);
  const [history, setHistory] = useState<string[]>([]);
  const [uppercase, setUppercase] = useState(false);
  const [hyphens, setHyphens] = useState(true);
  const [braces, setBraces] = useState(false);
  const [validatorInput, setValidatorInput] = useState("");

  const formatOpts = { uppercase, hyphens, braces };

  const getV5Namespace = (): string =>
    v5NamespaceKey === "custom" ? v5NamespaceCustom.trim() : V5_NAMESPACES[v5NamespaceKey as keyof typeof V5_NAMESPACES];

  const generateOne = useCallback((): string => {
    switch (version) {
      case "v1":
        return uuidv1();
      case "v4":
        return uuidv4();
      case "v5": {
        const ns = getV5Namespace();
        const name = v5Name.trim() || "default";
        return ns && uuidValidate(ns) ? uuidv5(name, ns) : uuidv5(name, V5_NAMESPACES.DNS);
      }
      case "v7":
        return uuidv7();
      default:
        return uuidv4();
    }
  }, [version, v5NamespaceKey, v5NamespaceCustom, v5Name]);

  const generateUUIDs = useCallback(() => {
    const ns = version === "v5" ? getV5Namespace() : "";
    if (version === "v5" && (!ns || !uuidValidate(ns))) {
      toast.error("Enter a valid namespace UUID for v5");
      return;
    }
    const quantity = Math.min(100, Math.max(1, count));
    const uuids: string[] = [];
    for (let i = 0; i < quantity; i++) {
      uuids.push(generateOne());
    }
    setGeneratedUUIDs(uuids);
    setHistory((prev) => [...uuids, ...prev].slice(0, HISTORY_SIZE));
    toast.success(`${quantity} UUID${quantity === 1 ? "" : "s"} generated`);
  }, [version, count, generateOne]);

  const clearAll = useCallback(() => {
    setGeneratedUUIDs([]);
    toast.info("Output cleared");
  }, []);

  const clearHistory = useCallback(() => {
    setHistory([]);
    toast.info("History cleared");
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (e.code === "Space" || e.code === "Enter") {
        e.preventDefault();
        generateUUIDs();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [generateUUIDs]);

  const download = useCallback(
    (format: "txt" | "csv") => {
      if (generatedUUIDs.length === 0) {
        toast.error("Generate UUIDs first");
        return;
      }
      const formatted = generatedUUIDs.map((u) => formatUUID(u, formatOpts));
      const content = format === "csv" ? formatted.map((u) => `"${u}"`).join("\n") : formatted.join("\n");
      const blob = new Blob([content], { type: format === "csv" ? "text/csv" : "text/plain" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `uuids-${version}-${new Date().toISOString().slice(0, 10)}.${format}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`Downloaded as .${format}`);
    },
    [generatedUUIDs, formatOpts, version]
  );

  const validatorRaw = validatorInput.trim().replace(/^\{|\}$/g, "");
  const validatorValid = validatorRaw ? uuidValidate(validatorRaw) : null;
  const validatorVersion = validatorRaw && validatorValid ? uuidVersion(validatorRaw) : null;
  const decodedDate =
    validatorRaw && validatorValid && validatorVersion !== undefined
      ? validatorVersion === 1
        ? decodeV1Timestamp(validatorRaw)
        : validatorVersion === 7
          ? decodeV7Timestamp(validatorRaw)
          : null
      : null;

  const displayUuids = generatedUUIDs.map((u) => formatUUID(u, formatOpts));
  const singleUUID = generatedUUIDs.length === 1 ? displayUuids[0] : null;
  const bulkCopyText = displayUuids.join("\n");

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Generator */}
        <Card className="card-enhanced">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Key className="h-5 w-5" />
              Generator
            </CardTitle>
            <CardDescription>Generate v1, v4, v5, or v7 UUIDs</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Version</Label>
              <Select value={version} onValueChange={(v) => setVersion(v as UUIDVersion)}>
                <SelectTrigger className="rounded-lg">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="v1">v1 (Time-based)</SelectItem>
                  <SelectItem value="v4">v4 (Random)</SelectItem>
                  <SelectItem value="v5">v5 (Namespace + name)</SelectItem>
                  <SelectItem value="v7">v7 (Unix-time ordered)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {version === "v5" && (
              <>
                <div className="space-y-2">
                  <Label>Namespace</Label>
                  <Select value={v5NamespaceKey} onValueChange={(v) => setV5NamespaceKey(v as V5NamespaceKey)}>
                    <SelectTrigger className="rounded-lg">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="DNS">DNS</SelectItem>
                      <SelectItem value="URL">URL</SelectItem>
                      <SelectItem value="OID">OID</SelectItem>
                      <SelectItem value="X500">X.500</SelectItem>
                      <SelectItem value="custom">Custom UUID</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {v5NamespaceKey === "custom" && (
                  <div className="space-y-2">
                    <Label>Namespace UUID</Label>
                    <Input
                      value={v5NamespaceCustom}
                      onChange={(e) => setV5NamespaceCustom(e.target.value)}
                      placeholder="e.g. 6ba7b810-9dad-11d1-80b4-00c04fd430c8"
                      className="font-mono rounded-lg"
                    />
                  </div>
                )}
                <div className="space-y-2">
                  <Label>Name</Label>
                  <Input
                    value={v5Name}
                    onChange={(e) => setV5Name(e.target.value)}
                    placeholder="Name string for v5"
                    className="rounded-lg"
                  />
                </div>
              </>
            )}

            <QuantitySlider
              label="Quantity"
              value={count}
              min={1}
              max={100}
              onValueChange={setCount}
              hint="1–100 UUIDs"
            />

            <div className="flex flex-wrap gap-2">
              <Button onClick={generateUUIDs} className="rounded-lg">
                <RefreshCw className="h-4 w-4 mr-2" />
                Generate
              </Button>
              <Button onClick={clearAll} variant="outline" className="rounded-lg">
                <RotateCcw className="h-4 w-4 mr-2" />
                Clear
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">Press Space or Enter to generate</p>
          </CardContent>
        </Card>

        {/* Output */}
        <Card className="card-enhanced">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5" />
              Output
            </CardTitle>
            <CardDescription>Copy or download generated UUIDs</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {singleUUID !== null ? (
              <div className="p-4 rounded-xl bg-muted/40 border">
                <p className="text-2xl sm:text-3xl font-semibold break-all tracking-tight [font-family:var(--font-jetbrains-mono)]">
                  {singleUUID}
                </p>
                <div className="mt-3 flex gap-2">
                  <CopyButton
                    text={bulkCopyText}
                    variant="outline"
                    size="sm"
                    onCopy={() => toast.success("Copied!")}
                  >
                    Copy
                  </CopyButton>
                </div>
              </div>
            ) : generatedUUIDs.length > 0 ? (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {displayUuids.map((uuid, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between gap-2 p-2 bg-muted/50 rounded-lg text-sm [font-family:var(--font-jetbrains-mono)]"
                  >
                    <span className="break-all min-w-0">{uuid}</span>
                    <CopyButton
                      text={uuid}
                      variant="ghost"
                      size="icon"
                      className="shrink-0"
                      showTextOnCopy={false}
                      onCopy={() => toast.success("Copied!")}
                    />
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground rounded-xl border border-dashed">
                <Sparkles className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>Generate UUIDs to see output here</p>
              </div>
            )}

            {generatedUUIDs.length > 0 && (
              <div className="flex flex-wrap gap-2">
                <CopyButton
                  text={bulkCopyText}
                  variant="default"
                  size="sm"
                  className="rounded-lg"
                  onCopy={() => toast.success("All copied!")}
                >
                  Copy All
                </CopyButton>
                <Button variant="outline" size="sm" className="rounded-lg" onClick={() => download("txt")}>
                  <Download className="h-4 w-4 mr-2" />
                  .txt
                </Button>
                <Button variant="outline" size="sm" className="rounded-lg" onClick={() => download("csv")}>
                  <Download className="h-4 w-4 mr-2" />
                  .csv
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Formatting */}
      <Card className="card-enhanced">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Hash className="h-5 w-5" />
            Formatting
          </CardTitle>
          <CardDescription>Display and copy options</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-6">
            <div className="flex items-center gap-2">
              <Switch id="upper" checked={uppercase} onCheckedChange={setUppercase} />
              <Label htmlFor="upper" className="cursor-pointer">Uppercase</Label>
            </div>
            <div className="flex items-center gap-2">
              <Switch id="hyphens" checked={hyphens} onCheckedChange={setHyphens} />
              <Label htmlFor="hyphens" className="cursor-pointer">With hyphens</Label>
            </div>
            <div className="flex items-center gap-2">
              <Switch id="braces" checked={braces} onCheckedChange={setBraces} />
              <Label htmlFor="braces" className="cursor-pointer">Braces &#123;uuid&#125;</Label>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Validator */}
      <Card className="card-enhanced">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CheckCircle className="h-5 w-5" />
            Validator
          </CardTitle>
          <CardDescription>Paste a UUID to check validity and version</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Input
            value={validatorInput}
            onChange={(e) => setValidatorInput(e.target.value)}
            placeholder="Paste UUID here..."
            className="rounded-lg [font-family:var(--font-jetbrains-mono)]"
          />
          {validatorInput.trim() && (
            <div className="flex flex-wrap items-center gap-4 p-4 rounded-xl bg-muted/30">
              {validatorValid ? (
                <>
                  <span className="flex items-center gap-2 text-green-600 dark:text-green-400">
                    <CheckCircle className="h-4 w-4" />
                    Valid
                  </span>
                  {validatorVersion !== undefined && (
                    <span className="text-sm font-medium">Version: {validatorVersion}</span>
                  )}
                  {decodedDate && (
                    <span className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Clock className="h-4 w-4" />
                      Decoded time: {decodedDate.toISOString()}
                    </span>
                  )}
                </>
              ) : (
                <span className="flex items-center gap-2 text-destructive">
                  <XCircle className="h-4 w-4" />
                  Invalid UUID
                </span>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* History */}
      {history.length > 0 && (
        <Card className="card-enhanced">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle>Session history</CardTitle>
              <CardDescription>Last {history.length} generated (session only)</CardDescription>
            </div>
            <Button variant="outline" size="sm" className="rounded-lg" onClick={clearHistory}>
              Clear history
            </Button>
          </CardHeader>
          <CardContent>
            <div className="space-y-1 max-h-40 overflow-y-auto text-sm [font-family:var(--font-jetbrains-mono)]">
              {history.slice(0, 20).map((u, i) => (
                <div key={i} className="flex items-center justify-between gap-2 py-1">
                  <span className="break-all text-muted-foreground">{formatUUID(u, formatOpts)}</span>
                  <CopyButton text={formatUUID(u, formatOpts)} variant="ghost" size="icon" showTextOnCopy={false} />
                </div>
              ))}
              {history.length > 20 && (
                <p className="text-xs text-muted-foreground pt-2">+{history.length - 20} more</p>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Special UUIDs */}
      <Card className="card-enhanced">
        <CardHeader>
          <CardTitle>Reference UUIDs</CardTitle>
          <CardDescription>Nil and Max UUIDs</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="p-4 rounded-xl bg-muted/40 flex flex-col gap-2">
              <p className="text-xs font-medium text-muted-foreground">Nil UUID</p>
              <p className="text-sm break-all [font-family:var(--font-jetbrains-mono)]">
                {formatUUID(NIL_UUID, formatOpts)}
              </p>
              <CopyButton text={formatUUID(NIL_UUID, formatOpts)} variant="outline" size="sm" showTextOnCopy={false} />
            </div>
            <div className="p-4 rounded-xl bg-muted/40 flex flex-col gap-2">
              <p className="text-xs font-medium text-muted-foreground">Max UUID</p>
              <p className="text-sm break-all [font-family:var(--font-jetbrains-mono)]">
                {formatUUID(MAX_UUID, formatOpts)}
              </p>
              <CopyButton text={formatUUID(MAX_UUID, formatOpts)} variant="outline" size="sm" showTextOnCopy={false} />
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
